import { ApiResponse } from 'apisauce';
import { CalendarEvent } from 'shovel-lib/types';
import { shovelAiNetwork } from './network';
import { sameAction } from './activityLogApi';
import { ExportCourseResponse } from './lambdaApi';
import { PlanningPreferences, PlanningRule } from '@utils/taskPlanning';

const syllabusBasePath = `/syllabus`;
const tasksBasePath = `/tasks`;

type ParseSyllabusRequest = {
  fileName: string;
  courseId: number;
  timezone: string;
  extractText?: boolean;
  developerMessage?: string;
  model?: string;
};

type AcknowledgeParsingResultRequest = {
  fileName: string;
  createdAt: Date;
  imported?: boolean;
};

export type SyllabusImportStatusItem = {
  courseId: number;
  fileName: string;
  createdAt: Date;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  imported?: boolean | null;
  failureStage?: 'parsing' | 'import' | null;
};

export type SyllabusImportStatusResponse = {
  items: SyllabusImportStatusItem[];
};

type TaskCategory = {
  id: number;
  name: string;
  emoji: string;
};

export type TaskCategorizationRequest = {
  existingCategories: TaskCategory[];
  tasks: {
    id: number;
    title: string;
    subtitle: string;
  }[];
  developerMessage?: string;
};

type TaskCategoryMapping = {
  taskId: number;
  categoryId: number;
};

type TaskCategorizationResponse = {
  newCategories: TaskCategory[];
  taskCategoryMappings: TaskCategoryMapping[];
};

export type PlanThisWeekRequest = {
  semesterId: number;
  studyBlocks: {
    id: number;
    start: Date;
    end: Date;
  }[];
  calendarEvents: {
    title?: string;
    type: string;
    start: Date | string;
    end: Date | string;
    courseName?: string;
    courseId?: number;
  }[];
  tasks: {
    id: number;
    title: string;
    dueDate: Date;
    earliestStart: Date;
    timeNeed: number;
    // How much of the task is behind the student already, done or on the calendar: sessions are sized for the
    // whole task, so a ramp carries on from here rather than starting again each week.
    progressMinutes: number;
    /** A review session: its days are Shovel's own, so the scheduler doesn't count it against `finishDaysAhead`. */
    isReview?: boolean;
    priority: number;
    courseName?: string;
    courseId?: number;
    blockIds: number[];
  }[];
  timeZone: string;
  weekEnd: Date;
  planningPreferences: PlanningPreferences;
  plannedBlocks: { start: Date | string; end: Date | string }[];
  /** The student's awake times in these dates, for the time around sleep. */
  awakeTimes: { start: Date | string; end: Date | string }[];
  /** Where each busy event in these dates ends, unless a commute follows it: for the break after it. */
  eventEnds: (Date | string)[];
  userPrompt?: string;
  developerMessage?: string;
  reasoningEffort?: 'minimal' | 'low' | 'medium' | 'high';
};

export type UnplannedTask = {
  taskId: number;
  minutes: number;
};

/**
 * A preference that stayed on and still lost sessions. `brokenRules` is what the scheduler had to switch off for
 * the work to fit; this is the quieter half - the rule was honoured where it could be, and `missed` of the
 * `sessions` it had any say in went the other way.
 */
export type RuleMisses = {
  rule: PlanningRule;
  sessions: number;
  missed: number;
};

// A dry run: the blocks the scheduler would create, not saved.
export type PlanPreviewResponse = {
  plannedTasks: { taskId: number; starts: string; ends: string }[];
  unplanned?: UnplannedTask[];
  brokenRules?: string[];
  ruleMisses?: RuleMisses[];
};

type PlanTasksResponse = {
  plannedTasks: CalendarEvent[];
  unplanned?: UnplannedTask[];
  brokenRules?: string[];
  ruleMisses?: RuleMisses[];
};

const assistantBasePath = `/assistant`;

export type AssistantHistoryMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type AssistantChatRequest = {
  message: string;
  conversationId?: string;
  history?: AssistantHistoryMessage[];
};

export type AssistantPendingAction = {
  actionType: string;
  summary: string;
  confirmationToken: string;
};

export type AssistantChatResponse = {
  reply: string;
  conversationId: string;
  toolsUsed?: string[];
  pendingActions?: AssistantPendingAction[];
};

export type AssistantConfirmActionResponse = {
  success: boolean;
  actionType: string;
  summary: string;
  result?: Record<string, unknown> | unknown[] | null;
};

export interface ShovelAiApi {
  getSyllabusFileUploadUrl: (
    fileName: string,
    fileType: string
  ) => Promise<ApiResponse<{ uploadUrl: string; fileName: string }>>;
  parseSyllabus: (request: ParseSyllabusRequest) => Promise<ApiResponse<void>>;
  getSyllabusImportStatus: () => Promise<ApiResponse<SyllabusImportStatusResponse>>;
  getParsedSyllabusContent: (fileName: string) => Promise<ApiResponse<ExportCourseResponse>>;
  acknowledgeSyllabusImportResult: (request: AcknowledgeParsingResultRequest) => Promise<ApiResponse<void>>;

  categorizeTasks: (request: TaskCategorizationRequest) => Promise<ApiResponse<TaskCategorizationResponse>>;

  planTasks: (request: PlanThisWeekRequest, actionId?: string) => Promise<ApiResponse<PlanTasksResponse>>;

  previewPlan: (request: PlanThisWeekRequest) => Promise<ApiResponse<PlanPreviewResponse>>;

  assistantChat: (request: AssistantChatRequest) => Promise<ApiResponse<AssistantChatResponse>>;
  confirmAssistantAction: (confirmationToken: string) => Promise<ApiResponse<AssistantConfirmActionResponse>>;
}

const getSyllabusFileUploadUrl = (fileName: string, fileType: string) => {
  const params = new URLSearchParams({ fileName, fileType });
  return shovelAiNetwork.get<{ uploadUrl: string; fileName: string }>(`${syllabusBasePath}/upload-url?${params.toString()}`);
};

const parseSyllabus = (request: ParseSyllabusRequest) => shovelAiNetwork.post<void>(`${syllabusBasePath}/parse`, request);

const getSyllabusImportStatus = () => shovelAiNetwork.get<SyllabusImportStatusResponse>(`${syllabusBasePath}/import-status`);

const getParsedSyllabusContent = (fileName: string) => {
  const params = new URLSearchParams({ fileName });
  return shovelAiNetwork.get<ExportCourseResponse>(`${syllabusBasePath}/parsed-content?${params.toString()}`);
};

const acknowledgeSyllabusImportResult = (request: AcknowledgeParsingResultRequest) =>
  shovelAiNetwork.put<void>(`${syllabusBasePath}/acknowledge-parsing-result`, request);

const categorizeTasks = (request: TaskCategorizationRequest) =>
  shovelAiNetwork.post<TaskCategorizationResponse>(`${tasksBasePath}/categorize`, request);

// `actionId`: a Replan's unplan, so the run and what it took off are one change for Undo (activity log).
const planTasks = (request: PlanThisWeekRequest, actionId?: string) =>
  shovelAiNetwork.post<PlanTasksResponse>(`${tasksBasePath}/plan`, request, { headers: sameAction(actionId) });

const previewPlan = (request: PlanThisWeekRequest) =>
  shovelAiNetwork.post<PlanPreviewResponse>(`${tasksBasePath}/plan`, { ...request, dryRun: true });

const assistantChat = (request: AssistantChatRequest) =>
  shovelAiNetwork.post<AssistantChatResponse>(`${assistantBasePath}/chat`, request);

const confirmAssistantAction = (confirmationToken: string) =>
  shovelAiNetwork.post<AssistantConfirmActionResponse>(`${assistantBasePath}/actions/confirm`, { confirmationToken });

export const shovelAiApi: ShovelAiApi = {
  getSyllabusFileUploadUrl,
  parseSyllabus,
  getSyllabusImportStatus,
  getParsedSyllabusContent,
  acknowledgeSyllabusImportResult,
  categorizeTasks,
  planTasks,
  previewPlan,
  assistantChat,
  confirmAssistantAction
};
